import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCategoryColor, getStatusColor } from '../utils/constants';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSend, FiCheck, FiLoader, FiAlertTriangle, FiPlus, FiCheckCircle, FiMessageCircle } = FiIcons;

const QuestionDetails = ({ questionId, onNavigate }) => {
  const [newAnswer, setNewAnswer] = useState('');
  const [newExtensionQuestion, setNewExtensionQuestion] = useState('');
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [extensionQuestions, setExtensionQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittingExtension, setSubmittingExtension] = useState(false);
  const [user, setUser] = useState(null);
  const [showExtensionForm, setShowExtensionForm] = useState(false);

  useEffect(() => {
    if (questionId) {
      fetchQuestionDetails();
      getCurrentUser();
    }
  }, [questionId]);

  const getCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const fetchQuestionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch question details
      const { data: questionData, error: questionError } = await supabase
        .from('questions_kb2024')
        .select('*')
        .eq('id', questionId)
        .single();

      if (questionError) {
        console.error('Question fetch error:', questionError);
        throw new Error(`Failed to load question: ${questionError.message}`);
      }

      if (!questionData) {
        throw new Error('Question not found');
      }

      setQuestion(questionData);

      // Fetch answers for this question
      const { data: answersData, error: answersError } = await supabase
        .from('answers_kb2024')
        .select('id, question_id, answer_text, answered_by, created_at')
        .eq('question_id', questionId)
        .order('created_at', { ascending: true });

      if (answersError && answersError.code !== 'PGRST116') {
        console.error('Answers fetch error:', answersError);
        setAnswers([]);
      } else {
        if (answersData && answersData.length > 0) {
          const { data: extendedAnswersData, error: extendedError } = await supabase
            .from('answers_kb2024')
            .select('*')
            .eq('question_id', questionId)
            .order('created_at', { ascending: true });

          if (extendedError) {
            console.log('Using basic answer data due to schema limitations');
            setAnswers(answersData || []);
          } else {
            setAnswers(extendedAnswersData || []);
          }
        } else {
          setAnswers([]);
        }
      }

      // Fetch extension questions for this question
      const { data: extensionData, error: extensionError } = await supabase
        .from('extension_questions_kb2024')
        .select('*')
        .eq('parent_question_id', questionId)
        .order('created_at', { ascending: true });

      if (extensionError) {
        console.error('Extension questions fetch error:', extensionError);
        setExtensionQuestions([]);
      } else {
        setExtensionQuestions(extensionData || []);
      }

    } catch (error) {
      console.error('Error fetching question details:', error);
      setError(error.message || 'Unable to load question details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getUserRole = () => {
    return user?.user_metadata?.user_role || 'User';
  };

  const isExpertUser = () => {
    const role = getUserRole();
    return role === 'Maersk Team' || role === 'Mariapps SME';
  };

  const canVerifyAnswers = () => {
    const role = getUserRole();
    return role === 'Maersk Team' || role === 'Mariapps SME';
  };

  const canAddExtensionQuestion = () => {
    return question && question.status === 'Open' && user;
  };

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    if (!newAnswer.trim() || !user) return;

    try {
      setSubmitting(true);
      setError(null);

      const userIdentifier = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
      const userRole = getUserRole();
      const isExpert = isExpertUser();

      console.log('Submitting answer with data:', {
        questionId,
        userIdentifier,
        userRole,
        isExpert,
        answerLength: newAnswer.trim().length
      });

      // Try to insert with all columns first
      let answerData = null;
      let answerError = null;

      // First try with full schema
      const fullInsertResult = await supabase
        .from('answers_kb2024')
        .insert([
          {
            question_id: questionId,
            answer_text: newAnswer.trim(),
            answered_by: userIdentifier,
            user_role: userRole,
            is_verified: isExpert,
            created_at: new Date().toISOString()
          }
        ])
        .select();

      if (fullInsertResult.error) {
        console.log('Full schema insert failed, trying basic schema:', fullInsertResult.error.message);
        
        // Fallback to basic schema
        const basicInsertResult = await supabase
          .from('answers_kb2024')
          .insert([
            {
              question_id: questionId,
              answer_text: newAnswer.trim(),
              answered_by: userIdentifier,
              created_at: new Date().toISOString()
            }
          ])
          .select();

        answerData = basicInsertResult.data;
        answerError = basicInsertResult.error;

        if (answerError) {
          throw new Error(`Failed to submit answer: ${answerError.message}`);
        }

        console.log('Answer submitted with basic schema');
      } else {
        answerData = fullInsertResult.data;
        console.log('Answer submitted with full schema');
      }

      // Update question status based on who answered
      let newStatus = 'In Progress';
      if (isExpert) {
        newStatus = 'Answered';
      } else if (question.status === 'Open') {
        newStatus = 'In Progress';
      }

      // Update question status if it needs to change
      if (newStatus !== question.status) {
        const { error: updateError } = await supabase
          .from('questions_kb2024')
          .update({ status: newStatus })
          .eq('id', questionId);

        if (updateError) {
          console.error('Status update error:', updateError);
          console.warn('Answer submitted but status update failed');
        } else {
          setQuestion({ ...question, status: newStatus });
        }
      }

      // Add the new answer to local state
      if (answerData && answerData[0]) {
        const newAnswerWithDefaults = {
          ...answerData[0],
          user_role: userRole,
          is_verified: isExpert
        };
        setAnswers([...answers, newAnswerWithDefaults]);
      }

      // Clear the form
      setNewAnswer('');
      
      console.log('Answer submitted successfully');

    } catch (error) {
      console.error('Error submitting answer:', error);
      setError(error.message || 'Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitExtensionQuestion = async (e) => {
    e.preventDefault();
    if (!newExtensionQuestion.trim() || !user) return;

    try {
      setSubmittingExtension(true);
      setError(null);

      const userIdentifier = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';

      const { data, error: submitError } = await supabase
        .from('extension_questions_kb2024')
        .insert([
          {
            parent_question_id: questionId,
            extension_text: newExtensionQuestion.trim(),
            asked_by: userIdentifier,
            created_at: new Date().toISOString()
          }
        ])
        .select();

      if (submitError) {
        throw new Error(`Failed to submit extension question: ${submitError.message}`);
      }

      // Add the new extension question to local state
      if (data && data[0]) {
        setExtensionQuestions([...extensionQuestions, data[0]]);
      }

      // Clear the form and hide it
      setNewExtensionQuestion('');
      setShowExtensionForm(false);
      
      console.log('Extension question submitted successfully');

    } catch (error) {
      console.error('Error submitting extension question:', error);
      setError(error.message || 'Failed to submit extension question. Please try again.');
    } finally {
      setSubmittingExtension(false);
    }
  };

  const handleVerifyAnswer = async (answerId) => {
    try {
      const verifierName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
      
      // Mark answer as verified
      const { error: verifyError } = await supabase
        .from('answers_kb2024')
        .update({ 
          is_verified: true,
          verified_by: verifierName,
          verified_at: new Date().toISOString()
        })
        .eq('id', answerId);

      if (verifyError) {
        console.error('Verification error:', verifyError);
        throw new Error(`Failed to verify answer: ${verifyError.message}`);
      }

      // Update question status to Answered
      const { error: statusError } = await supabase
        .from('questions_kb2024')
        .update({ status: 'Answered' })
        .eq('id', questionId);

      if (statusError) {
        console.error('Status update error:', statusError);
        console.warn('Answer verified but status update failed');
      }

      // Update local state
      setQuestion({ ...question, status: 'Answered' });
      setAnswers(answers.map(answer => 
        answer.id === answerId 
          ? { 
              ...answer, 
              is_verified: true, 
              verified_by: verifierName,
              verified_at: new Date().toISOString()
            }
          : answer
      ));

      console.log('Answer verified and question marked as answered');
    } catch (error) {
      console.error('Error verifying answer:', error);
      setError(error.message || 'Failed to verify answer. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAnswerStatusBadge = (answer) => {
    if (answer.is_verified) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          <SafeIcon icon={FiCheckCircle} className="w-3 h-3" />
          Verified
        </span>
      );
    }
    
    const answerUserRole = answer.user_role || 'User';
    if (answerUserRole === 'Maersk Team' || answerUserRole === 'Mariapps SME') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          Expert Answer
        </span>
      );
    }
    
    return (
      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
        Pending Verification
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading question...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <SafeIcon icon={FiAlertTriangle} className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Question</h3>
          <p className="text-red-700 mb-4">{error || 'Question not found'}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => onNavigate('questions')}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Back to Questions
            </button>
            <button
              onClick={fetchQuestionDetails}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('questions')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Questions
        </button>

        {/* Create New Question Button */}
        <div className="mb-4">
          <button
            onClick={() => onNavigate('question-form')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            Ask a Question
          </button>
        </div>
      </motion.div>

      {/* Schema Warning */}
      {error && error.includes('user_role') && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6"
        >
          <div className="flex items-center gap-2 text-yellow-800 mb-2">
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
            <span className="font-medium">Database Schema Update Required</span>
          </div>
          <p className="text-yellow-700 text-sm mb-3">
            The answers table needs to be updated with new workflow columns. Run the fix script to resolve this issue.
          </p>
          <button
            onClick={() => {
              console.log('Run: node fix-answers-table.js');
              alert('Please run: node fix-answers-table.js in your terminal and follow the SQL instructions.');
            }}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-1 px-3 rounded text-sm"
          >
            Show Fix Instructions
          </button>
        </motion.div>
      )}

      {/* Question Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8"
      >
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-2xl font-bold text-gray-900">{question.title}</h1>
          <div className="flex gap-2">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryColor(question.category)}`}>
              {question.category}
            </span>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(question.status)}`}>
              {question.status}
            </span>
          </div>
        </div>

        <p className="text-gray-700 mb-6 leading-relaxed whitespace-pre-line">{question.description}</p>

        <div className="text-sm text-gray-600">
          Asked by <span className="font-medium">{question.asked_by}</span> on {formatDate(question.created_at)}
        </div>
      </motion.div>

      {/* Extension Questions Section */}
      {extensionQuestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-orange-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Extension Questions ({extensionQuestions.length})
              </h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">Additional questions related to the main topic</p>
          </div>
          <div className="divide-y divide-gray-100">
            {extensionQuestions.map((extQuestion, index) => (
              <motion.div
                key={extQuestion.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMessageCircle} className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 leading-relaxed whitespace-pre-line mb-3">
                      {extQuestion.extension_text}
                    </p>
                    <div className="text-sm text-gray-600">
                      Asked by <span className="font-medium">{extQuestion.asked_by}</span> on {formatDate(extQuestion.created_at)}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Add Extension Question Form */}
      {canAddExtensionQuestion() && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-8"
        >
          {!showExtensionForm ? (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-medium text-orange-900">Have a related question?</h3>
              </div>
              <p className="text-orange-700 mb-4">
                Add an extension question to clarify or expand on this topic while it's still open for discussion.
              </p>
              <button
                onClick={() => setShowExtensionForm(true)}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 mx-auto"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                Add Extension Question
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmitExtensionQuestion}>
              <div className="flex items-center gap-2 mb-3">
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-medium text-orange-900">Add Extension Question</h3>
              </div>
              <div className="mb-4">
                <textarea
                  value={newExtensionQuestion}
                  onChange={(e) => setNewExtensionQuestion(e.target.value)}
                  placeholder="Ask a follow-up question that's related to the main topic..."
                  rows={4}
                  disabled={submittingExtension}
                  className="w-full px-4 py-3 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 resize-none disabled:bg-orange-100"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submittingExtension || !newExtensionQuestion.trim()}
                  className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  {submittingExtension ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="w-4 h-4" />
                      Add Question
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowExtensionForm(false);
                    setNewExtensionQuestion('');
                  }}
                  disabled={submittingExtension}
                  className="py-2 px-4 border border-orange-300 text-orange-700 font-medium rounded-lg hover:bg-orange-100 transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </motion.div>
      )}

      {/* Status Workflow Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8"
      >
        <h3 className="text-sm font-medium text-blue-900 mb-2">Question Status Workflow</h3>
        <div className="flex items-center gap-4 text-xs text-blue-700">
          <div className={`flex items-center gap-1 ${question.status === 'Open' ? 'font-semibold' : ''}`}>
            <div className={`w-2 h-2 rounded-full ${question.status === 'Open' ? 'bg-blue-600' : 'bg-blue-300'}`}></div>
            Open
          </div>
          <div className="text-blue-400">→</div>
          <div className={`flex items-center gap-1 ${question.status === 'In Progress' ? 'font-semibold' : ''}`}>
            <div className={`w-2 h-2 rounded-full ${question.status === 'In Progress' ? 'bg-yellow-600' : 'bg-blue-300'}`}></div>
            In Progress
          </div>
          <div className="text-blue-400">→</div>
          <div className={`flex items-center gap-1 ${question.status === 'Answered' ? 'font-semibold' : ''}`}>
            <div className={`w-2 h-2 rounded-full ${question.status === 'Answered' ? 'bg-green-600' : 'bg-blue-300'}`}></div>
            Answered
          </div>
        </div>
        {question.status === 'Open' && (
          <p className="text-xs text-blue-600 mt-2">
            ✨ Extension questions can be added while the status is "Open"
          </p>
        )}
      </motion.div>

      {/* Answers Section */}
      {answers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8"
        >
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">
              Answers ({answers.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {answers.map((answer, index) => (
              <motion.div
                key={answer.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    {getAnswerStatusBadge(answer)}
                    {answer.user_role && (
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                        {answer.user_role}
                      </span>
                    )}
                  </div>
                  
                  {/* Verify Button for eligible users */}
                  {!answer.is_verified && canVerifyAnswers() && (
                    <button
                      onClick={() => handleVerifyAnswer(answer.id)}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-1 px-3 rounded-lg transition-colors duration-200 text-sm"
                    >
                      <SafeIcon icon={FiCheck} className="w-3 h-3" />
                      Verify Answer
                    </button>
                  )}
                </div>

                <p className="text-gray-700 leading-relaxed whitespace-pre-line mb-4">
                  {answer.answer_text}
                </p>

                <div className="text-sm text-gray-600">
                  <div>
                    Answered by <span className="font-medium">{answer.answered_by}</span> on {formatDate(answer.created_at)}
                  </div>
                  {answer.is_verified && answer.verified_by && (
                    <div className="mt-1 text-green-600">
                      Verified by <span className="font-medium">{answer.verified_by}</span>
                      {answer.verified_at && ` on ${formatDate(answer.verified_at)}`}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Answer Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Your Answer</h3>
        
        {isExpertUser() && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">
              <strong>Expert User:</strong> Your answer will be automatically verified and mark this question as answered.
            </p>
          </div>
        )}

        {error && !error.includes('user_role') && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmitAnswer}>
          <div className="mb-4">
            <textarea
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              placeholder="Share your knowledge and help answer this question..."
              rows={6}
              disabled={submitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none disabled:bg-gray-100"
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !newAnswer.trim() || !user}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <SafeIcon icon={FiSend} className="w-4 h-4" />
                Submit Answer
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default QuestionDetails;